import { useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Wallet,
  AlertTriangle,
  Ban,
  Clock,
  ExternalLink,
  RotateCcw,
  ShieldOff,
} from 'lucide-react';
import { Button, Loading, EmptyState, Badge, Modal, Input } from '@/components/ui';
import { formatNIS } from '@/utils/currency';
import { formatDate, formatDateTime } from '@/utils/date';
import {
  affiliateApi,
  mapPayoutAdminError,
  redemptionMethodLabel,
  type MarkPaidInput,
  type PartnerPenaltyRow,
  type PayoutRow,
  type PayoutStatus,
} from '@/api/affiliate';

const PAYOUT_VARIANT: Record<PayoutStatus, 'success' | 'warning' | 'info' | 'neutral' | 'danger'> = {
  pending: 'warning',
  held: 'danger',
  requested: 'info',
  approved: 'info',
  paid: 'success',
  invoiced: 'success',
};

function money(agorot: number | null | undefined): string {
  return formatNIS(agorot ?? 0);
}

function toLocalInputValue(iso?: string): string {
  const d = iso ? new Date(iso) : new Date();
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(value: string): string {
  const d = new Date(value);
  return d.toISOString();
}

function isPermissionError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /permission denied|sign in as a platform admin|unauthorized|forbidden/i.test(msg);
}

export default function PayoutsPage() {
  const qc = useQueryClient();
  const [period, setPeriod] = useState('');
  const [markPaid, setMarkPaid] = useState<{ open: boolean; payout?: PayoutRow }>({ open: false });
  const [rejectDoc, setRejectDoc] = useState<{ open: boolean; payout?: PayoutRow }>({ open: false });
  const [resetRow, setResetRow] = useState<{ open: boolean; payout?: PayoutRow }>({ open: false });
  const [waive, setWaive] = useState<{ open: boolean; penalty?: PartnerPenaltyRow }>({ open: false });

  const periodsQ = useQuery({
    queryKey: ['affiliate-payout-periods'],
    queryFn: () => affiliateApi.payouts.listPeriods(),
  });

  const payoutsQ = useQuery({
    queryKey: ['affiliate-payouts', period],
    queryFn: () => affiliateApi.payouts.listByPeriod(period || undefined),
  });

  const penaltiesQ = useQuery({
    queryKey: ['affiliate-pending-penalties'],
    queryFn: () => affiliateApi.payouts.listPendingPenalties(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['affiliate-payouts'] });
    qc.invalidateQueries({ queryKey: ['affiliate-payout-periods'] });
    qc.invalidateQueries({ queryKey: ['affiliate-pending-penalties'] });
    qc.invalidateQueries({ queryKey: ['affiliate-partner-payouts'] });
  };

  const markPaidMut = useMutation({
    mutationFn: ({ id, input }: { id: string; input: MarkPaidInput }) =>
      affiliateApi.payouts.markPaid(id, input),
    onSuccess: () => {
      toast.success('Payout marked paid');
      setMarkPaid({ open: false });
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      affiliateApi.payouts.rejectDocument(id, reason),
    onSuccess: () => {
      toast.success('Document rejected');
      setRejectDoc({ open: false });
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  const resetMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      affiliateApi.payouts.resetRedemption(id, reason),
    onSuccess: () => {
      toast.success('Redemption reset');
      setResetRow({ open: false });
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  const waiveMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      affiliateApi.payouts.waivePenalty(id, reason),
    onSuccess: () => {
      toast.success('Penalty waived');
      setWaive({ open: false });
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message ?? 'Failed'),
  });

  const busy =
    markPaidMut.isPending || rejectMut.isPending || resetMut.isPending || waiveMut.isPending;

  const payouts = payoutsQ.data ?? [];
  const lateCount = useMemo(() => payouts.filter((p) => p.receipt_late).length, [payouts]);
  const heldDiscountCount = useMemo(() => payouts.filter((p) => p.discount_held).length, [payouts]);
  const heldByLateCount = useMemo(
    () => payouts.filter((p) => p.held_by_late_receipt).length,
    [payouts],
  );

  const loadError = payoutsQ.error ?? periodsQ.error ?? penaltiesQ.error;
  const permissionDenied = loadError ? isPermissionError(loadError) : false;

  return (
    <div className="p-6 space-y-4" dir="ltr">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-indigo-400" />
          <div>
            <h1 className="text-xl font-semibold text-white">Partner Payouts</h1>
            <p className="text-xs text-ink-muted mt-0.5">
              Mark cash rewards paid, reject documents, waive late-receipt penalties, or reset a
              mistaken redemption
            </p>
          </div>
        </div>
        <div>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-10 px-3 rounded border border-navy-border bg-navy-deeper text-ink-primary text-sm"
          >
            <option value="">All periods</option>
            {(periodsQ.data ?? []).map((p) => (
              <option key={p} value={p}>
                {formatDate(p)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {(lateCount > 0 || heldByLateCount > 0 || heldDiscountCount > 0 || (penaltiesQ.data?.length ?? 0) > 0) && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {lateCount > 0 && (
            <SummaryChip
              tone="danger"
              icon={<Clock size={14} />}
              label={`${lateCount} late receipt${lateCount === 1 ? '' : 's'}`}
            />
          )}
          {heldByLateCount > 0 && (
            <SummaryChip
              tone="warning"
              icon={<Ban size={14} />}
              label={`${heldByLateCount} payout${heldByLateCount === 1 ? '' : 's'} held for late receipt`}
            />
          )}
          {heldDiscountCount > 0 && (
            <SummaryChip
              tone="danger"
              icon={<AlertTriangle size={14} />}
              label={`${heldDiscountCount} discount balance${heldDiscountCount === 1 ? '' : 's'} held`}
            />
          )}
          {(penaltiesQ.data?.length ?? 0) > 0 && (
            <SummaryChip
              tone="warning"
              icon={<ShieldOff size={14} />}
              label={`${penaltiesQ.data!.length} pending penalt${penaltiesQ.data!.length === 1 ? 'y' : 'ies'}`}
            />
          )}
        </div>
      )}

      {(penaltiesQ.data?.length ?? 0) > 0 && (
        <div className="card overflow-x-auto border border-warning/30">
          <div className="px-4 py-3 border-b border-navy-border flex items-center gap-2">
            <ShieldOff className="h-4 w-4 text-warning" />
            <h2 className="text-sm font-semibold text-white">Pending late-receipt penalties</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted text-xs uppercase">
                <th className="px-4 py-2">Partner</th>
                <th className="px-4 py-2">Penalty</th>
                <th className="px-4 py-2">Created</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {penaltiesQ.data!.map((pen) => (
                <tr key={pen.id} className="border-t border-navy-border">
                  <td className="px-4 py-3 text-white">{pen.partner_name}</td>
                  <td className="px-4 py-3 num" dir="ltr">
                    {money(pen.penalty_agorot)}
                    <span className="text-ink-muted text-xs ms-2">({pen.reason})</span>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">{formatDateTime(pen.created_at)}</td>
                  <td className="px-4 py-3">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => setWaive({ open: true, penalty: pen })}
                    >
                      Waive penalty
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {payoutsQ.isLoading || periodsQ.isLoading ? (
        <Loading text="Loading payouts…" />
      ) : permissionDenied ? (
        <EmptyState
          icon={<ShieldOff className="h-8 w-8" />}
          title="Permission denied"
          description="Partner payouts require a signed-in platform admin session. Sign in as the Otomator admin and reload."
        />
      ) : loadError ? (
        <EmptyState
          icon={<AlertTriangle className="h-8 w-8" />}
          title="Could not load payouts"
          description={mapPayoutAdminError(loadError as { message?: string })}
        />
      ) : !payouts.length ? (
        <EmptyState
          icon={<Wallet className="h-8 w-8" />}
          title="No payouts"
          description="Monthly commission roll-ups will appear here once partners have rewards to redeem."
        />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-muted text-xs uppercase">
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Partner</th>
                <th className="px-4 py-3">Reward</th>
                <th className="px-4 py-3">Net / VAT / Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Document</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <PayoutRowView
                  key={p.id}
                  payout={p}
                  busy={busy}
                  onMarkPaid={() => setMarkPaid({ open: true, payout: p })}
                  onReject={() => setRejectDoc({ open: true, payout: p })}
                  onReset={() => setResetRow({ open: true, payout: p })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MarkPaidModal
        open={markPaid.open}
        payout={markPaid.payout}
        loading={markPaidMut.isPending}
        onClose={() => setMarkPaid({ open: false })}
        onSubmit={(input) => {
          if (!markPaid.payout) return;
          markPaidMut.mutate({ id: markPaid.payout.id, input });
        }}
      />

      <ReasonModal
        open={rejectDoc.open}
        title={
          rejectDoc.payout
            ? `Reject document — ${rejectDoc.payout.partner_name}`
            : 'Reject document'
        }
        confirmLabel="Reject document"
        danger
        loading={rejectMut.isPending}
        onClose={() => setRejectDoc({ open: false })}
        onSubmit={(reason) => {
          if (!rejectDoc.payout) return;
          rejectMut.mutate({ id: rejectDoc.payout.id, reason });
        }}
      />

      <ReasonModal
        open={resetRow.open}
        title={resetRow.payout ? `Reset redemption — ${resetRow.payout.partner_name}` : 'Reset'}
        confirmLabel="Reset redemption"
        loading={resetMut.isPending}
        secondaryNote="Undo only. This voids an unconsumed discount/credits choice and returns the payout to pending."
        onClose={() => setResetRow({ open: false })}
        onSubmit={(reason) => {
          if (!resetRow.payout) return;
          resetMut.mutate({ id: resetRow.payout.id, reason });
        }}
      />

      <ReasonModal
        open={waive.open}
        title={waive.penalty ? `Waive penalty — ${waive.penalty.partner_name}` : 'Waive penalty'}
        confirmLabel="Waive penalty"
        loading={waiveMut.isPending}
        onClose={() => setWaive({ open: false })}
        onSubmit={(reason) => {
          if (!waive.penalty) return;
          waiveMut.mutate({ id: waive.penalty.id, reason });
        }}
      />
    </div>
  );
}

function SummaryChip({
  tone,
  icon,
  label,
}: {
  tone: 'danger' | 'warning';
  icon: ReactNode;
  label: string;
}) {
  const cls =
    tone === 'danger'
      ? 'border-danger/40 bg-danger/10 text-danger'
      : 'border-warning/40 bg-warning/10 text-warning';
  return (
    <div className={`rounded-lg border px-3 py-2 text-xs font-medium inline-flex items-center gap-2 ${cls}`}>
      {icon}
      {label}
    </div>
  );
}

function PayoutRowView({
  payout,
  busy,
  onMarkPaid,
  onReject,
  onReset,
}: {
  payout: PayoutRow;
  busy: boolean;
  onMarkPaid: () => void;
  onReject: () => void;
  onReset: () => void;
}) {
  const net = payout.amount_net_agorot ?? payout.amount_agorot;
  const vat = payout.vat_agorot ?? 0;
  const total = payout.amount_gross_agorot ?? net + vat;
  const isCash = payout.redemption_method === 'cash_payment_request';
  const canMarkPaid =
    isCash &&
    (payout.status === 'requested' || payout.status === 'approved') &&
    payout.redemption_status !== 'document_rejected' &&
    !payout.paid_at;
  const canReject =
    isCash &&
    (payout.status === 'requested' || payout.status === 'approved') &&
    !payout.paid_at &&
    payout.redemption_status !== 'document_rejected';
  const canReset = !payout.paid_at || payout.redemption_method !== 'cash_payment_request';

  const rowTone = payout.discount_held
    ? 'bg-danger/5'
    : payout.receipt_late
      ? 'bg-warning/5'
      : payout.held_by_late_receipt
        ? 'bg-warning/[0.03]'
        : '';

  return (
    <tr className={`border-t border-navy-border align-top ${rowTone}`}>
      <td className="px-4 py-3 whitespace-nowrap">{formatDate(payout.period)}</td>
      <td className="px-4 py-3 text-white">
        <div className="flex flex-col gap-1.5">
          <span>{payout.partner_name}</span>
          {payout.receipt_late && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-warning">
              <Clock size={12} />
              Late on receipt
              {payout.receipt_due_at ? ` · due ${formatDate(payout.receipt_due_at)}` : ''}
            </span>
          )}
          {payout.held_by_late_receipt && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-warning">
              <Ban size={12} />
              Held — prior receipt still outstanding
            </span>
          )}
          {payout.discount_held && (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-danger">
              <AlertTriangle size={12} />
              Discount balance held (charge conflict) — do not retry
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-ink-primary">{redemptionMethodLabel(payout.redemption_method)}</span>
          <span className="text-[11px] text-ink-muted">
            {payout.commission_pct}% · {payout.active_subs_count} active subs
          </span>
          {payout.entity_kind_snapshot && (
            <span className="text-[11px] text-ink-muted">{payout.entity_kind_snapshot}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-0.5 num" dir="ltr">
          <span className="text-white font-medium">{money(total)}</span>
          <span className="text-[11px] text-ink-muted">
            net {money(net)}
            {vat > 0 ? ` · VAT ${money(vat)}` : ' · no VAT'}
          </span>
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col gap-1">
          <Badge variant={PAYOUT_VARIANT[payout.status]}>{payout.status}</Badge>
          <span className="text-[11px] text-ink-muted">{payout.redemption_status}</span>
          {payout.paid_at && (
            <span className="text-[11px] text-ink-muted">
              paid {formatDateTime(payout.paid_at)}
              {payout.paid_reference ? ` · ${payout.paid_reference}` : ''}
            </span>
          )}
          {payout.external_doc_rejected_reason && (
            <span className="text-[11px] text-danger">
              Rejected: {payout.external_doc_rejected_reason}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        {payout.external_doc_number || payout.invoice_number ? (
          <div className="flex flex-col gap-0.5">
            {(payout.external_doc_url || payout.invoice_url) ? (
              <a
                href={payout.external_doc_url || payout.invoice_url || '#'}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-gold hover:text-gold-light text-xs"
              >
                {payout.external_doc_number || payout.invoice_number} <ExternalLink size={11} />
              </a>
            ) : (
              <span className="text-xs text-ink-primary">
                {payout.external_doc_number || payout.invoice_number}
              </span>
            )}
            {payout.invoice_received_at && (
              <span className="text-[11px] text-ink-muted">
                receipt {formatDate(payout.invoice_received_at)}
              </span>
            )}
          </div>
        ) : payout.redemption_ref_type === 'billing_document' ? (
          <span className="text-xs text-ink-muted">Internal payment request</span>
        ) : (
          <span className="text-xs text-ink-muted">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex flex-col items-start gap-1.5">
          {canMarkPaid && (
            <Button size="sm" onClick={onMarkPaid} disabled={busy || payout.held_by_late_receipt}>
              Mark paid
            </Button>
          )}
          {canReject && (
            <Button size="sm" variant="secondary" onClick={onReject} disabled={busy}>
              Reject document
            </Button>
          )}
          {canReset && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={onReset}
              className="border border-navy-border text-ink-secondary"
            >
              <RotateCcw size={14} />
              Reset redemption
            </Button>
          )}
          {!canMarkPaid && !canReject && !canReset && (
            <span className="text-xs text-ink-muted">—</span>
          )}
        </div>
      </td>
    </tr>
  );
}

function MarkPaidModal({
  open,
  payout,
  loading,
  onClose,
  onSubmit,
}: {
  open: boolean;
  payout?: PayoutRow;
  loading: boolean;
  onClose: () => void;
  onSubmit: (input: MarkPaidInput) => void;
}) {
  const [paidAt, setPaidAt] = useState(toLocalInputValue());
  const [reference, setReference] = useState('');

  const reset = () => {
    setPaidAt(toLocalInputValue());
    setReference('');
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={payout ? `Mark paid — ${payout.partner_name}` : 'Mark paid'}
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-secondary">
          Marking paid is the approval. Record when the transfer happened and the bank/payment
          reference.
        </p>
        <Input
          label="Paid at"
          type="datetime-local"
          value={paidAt}
          onChange={(e) => setPaidAt(e.target.value)}
          required
        />
        <Input
          label="Payment reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          placeholder="Bank ref / transfer id"
          required
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={loading}
            onClick={() => {
              if (!paidAt) {
                toast.error('Paid-at is required');
                return;
              }
              if (!reference.trim()) {
                toast.error('Payment reference is required');
                return;
              }
              onSubmit({ paid_at: fromLocalInputValue(paidAt), reference: reference.trim() });
            }}
          >
            Mark paid
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ReasonModal({
  open,
  title,
  confirmLabel,
  loading,
  danger,
  secondaryNote,
  onClose,
  onSubmit,
}: {
  open: boolean;
  title: string;
  confirmLabel: string;
  loading: boolean;
  danger?: boolean;
  secondaryNote?: string;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Modal
      isOpen={open}
      onClose={() => {
        setReason('');
        onClose();
      }}
      title={title}
    >
      <div className="space-y-4">
        {secondaryNote && <p className="text-xs text-ink-muted">{secondaryNote}</p>}
        <Input
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            loading={loading}
            onClick={() => {
              if (!reason.trim()) {
                toast.error('A reason is required');
                return;
              }
              onSubmit(reason.trim());
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
